// Site copy — single source of truth.
// DEFAULTS feed the pages; the vault's Content tab overrides any key via /api/content.
// In text, *words* render as gold accents; newlines in *_bullets fields become list items.

export const DEFAULTS = {
  "intro.hero.eyebrow": "Harmonies of the Night",
  "intro.hero.subline":
    "Intelligent emotional music — intentional and deliberate. Custom TTRPG ambience, boss-battle themes and evocative soundscapes for dungeon masters, streamers and indie games, composed by *Paul Elliott Messier*, the composer & creative director honed in AAA quality assurance at Electronic Arts under Volt VMC. Music shaped to invoke the emotions and actions your story desires, made under a patient moon.",

  "intro.offering.heading": "Custom music for engaging tables & bold worlds",
  "intro.offering.card1_title": "What you get",
  "intro.offering.card1_body":
    "Tailor-made themes, seamless dungeon loops, boss-battle stingers and character motifs — polished masters built around your world, not pulled from a shelf.",
  "intro.offering.card2_title": "Who it is for",
  "intro.offering.card2_body":
    "Dungeon Masters who want their table to sound like Barovia, actual-play streamers building a signature sound, and indie developers scoring their first odyssey.",
  "intro.offering.card3_title": "Why custom",
  "intro.offering.card3_body":
    "Generic royalty-free tracks pull players out of the story. Music written for your campaign reacts to your party's choices and turns key moments into memories your table retells for years.",

  "intro.portraits.heading": "Under stage light & studio lamp",
  "intro.portraits.cap1": "Live — under the stage light",
  "intro.portraits.cap2": "Heavy heart, hushed strings",
  "intro.portraits.cap3": "Sunlit strings, same soul",
  "intro.portraits.cap4": "The den where the music comes home",

  "intro.manifesto.quote":
    "Composing intelligent emotional music — intentional and deliberate — for worlds that linger in the heart.",
  "intro.manifesto.known_as": "Known under the moon as Nox Harmonia",
  "intro.manifesto.body1":
    "Nox Harmonia is the alias under which Paul Elliott Messier the composer and creative director writes intelligent emotional music — scores that breathe, soar and stay with you long after the session ends. The work spans the biggest stages of the games industry and the most candlelit tables.",
  "intro.manifesto.body2":
    "Under Volt VMC, I started my journey as a P200 Quality Assurance Tester, later promoted to Central Online Services Management Team Member and shipped for Electronic Arts — shooters, racers and sci-fi odysseys under Volt VMC. Beyond the AAA wall, the journey carries indie soundtracks, tabletop roleplaying campaign music, and game design documents written and reviewed with a director's care.",
  "intro.manifesto.closing":
    "Whether your world needs a main theme, a heartbeat of rhythm, or a hand to guide the whole vision — I'm listening.",
  "intro.credits.line": "Quality Assurance & Test Leadership — Volt VMC for Electronic Arts",

  "portfolio.header.heading": "The Gallery Wall",
  "portfolio.header.body":
    "AAA titles shipped under *Volt VMC* for *Electronic Arts* — each one lit like a premiere night, framed like a one-sheet. My craft here: *quality assurance* — running tests, documenting, categorising and verifying glitches, reporting to higher management, building the physical builds, updating the machinery and managing a 20-tester crew. Below the wall: my own scores for indie worlds.",
  "portfolio.indie.heading": "Indie scores & sorcery",
  "portfolio.indie.body": "Original soundtracks for independent projects — adventures, mysteries and candlelit tables.",
  "portfolio.screening.heading": "Watch the music move",
  "portfolio.screening.body": "Scenes, showreels and scores in motion — shared straight from the YouTube channel.",
  "portfolio.cta.line": "Five premieres, one wall — every frame shipped to players worldwide.",

  "services.header.heading": "What the studio offers",
  "services.header.body":
    "Intelligent emotional music — intentional and deliberate — in five crafts. Choose what your world needs, then let the calculator shape a gentle estimate before we ever speak.",
  "services.calc.heading": "Estimate, then enlist",
  "services.calc.body":
    "Shape the estimate from your side of the moon — then send it with a few words and it arrives in my inbox with all the details intact.",

  "commission.hero.heading": "Music that moves the heart of your world",
  "commission.hero.body":
    "Intelligent emotional music — intentional and deliberate — for your campaign or game: TTRPG ambience, boss themes and full suites composed to invoke the emotions and actions your story desires. From the mists of Barovia to dragon-lit skies, Paul Elliott Messier scores the moments your table keeps forever.",
  "commission.why.card1_title": "What you get",
  "commission.why.card1_body":
    "Tailor-made themes, seamless dungeon loops, boss-battle stingers and character motifs — delivered as polished masters, built around your world, not pulled from a shelf.",
  "commission.why.card2_title": "Who it is for",
  "commission.why.card2_body":
    "Dungeon Masters who want their table to sound like Barovia, actual-play streamers building a signature sound, and indie developers scoring their first odyssey.",
  "commission.why.card3_title": "Why custom",
  "commission.why.card3_body":
    "Generic royalty-free tracks pull players out of the story. Music written for your campaign reacts to your party's choices and turns key moments into memories your table retells for years.",
  "commission.strahd.heading": "Curse of Strahd & Ravenloft custom suites",
  "commission.strahd.body":
    "You found me through the mists — most of my listeners did. I compose directly for DMs running Barovia: Strahd's organ-and-cello motif in new variations, crypt and castle ambience that loops seamlessly under the session, and combat stems that rise when the stakes do.",
  "commission.strahd.bullets":
    "Strahd organ & cello leitmotif variations\nDungeon, crypt and castle looping ambience\nInteractive combat stingers for Ravenloft boss fights\nTavern and safe-haven warmth between the shadows",
  "commission.pricing.heading": "What the music costs",
  "commission.pricing.body":
    "Starting points below — the final quote shifts with project scale, deadline and licensing. Shape an exact estimate in minutes with the quote calculator, or send the inquiry and I will price it with you.",
  "commission.pastwork.heading": "Hear what left the studio",
  "commission.pastwork.body": "Playing right now in the player below your screen — the full gallery holds the rest.",
  "commission.testimonials.heading": "Words the music brought back",
  "commission.support.heading": "Support the Music",
  "commission.support.body":
    "Not commissioning yet? If the music already lives at your table, an offering keeps it playing — and your words can join the public wall.",
  "commission.inquiry.heading": "Tell me about your world",
  "commission.inquiry.body":
    "Five fields, two minutes. The more atmosphere you describe, the faster the music finds it.",
  "commission.success.heading": "Your words are on their way",
  "commission.success.body":
    "Your inquiry has arrived, and I read every one personally. Expect a reply from paulelliottmessier@gmail.com as we shape the brief together.",

  "modal.support.heading": "Support the Music",
  "modal.support.body": "Every offering keeps the music playing — and your words join the wall below.",
};

export const GROUPS = [
  {
    page: "Intro (/)",
    sections: [
      {
        label: "Hero",
        fields: [
          { key: "intro.hero.eyebrow", label: "Eyebrow line" },
          { key: "intro.hero.subline", label: "Intro paragraph", multiline: true },
        ],
      },
      {
        label: "The Offering",
        fields: [
          { key: "intro.offering.heading", label: "Heading" },
          { key: "intro.offering.card1_title", label: "Card 1 title" },
          { key: "intro.offering.card1_body", label: "Card 1 body", multiline: true },
          { key: "intro.offering.card2_title", label: "Card 2 title" },
          { key: "intro.offering.card2_body", label: "Card 2 body", multiline: true },
          { key: "intro.offering.card3_title", label: "Card 3 title" },
          { key: "intro.offering.card3_body", label: "Card 3 body", multiline: true },
        ],
      },
      {
        label: "Portraits",
        fields: [
          { key: "intro.portraits.heading", label: "Heading" },
          { key: "intro.portraits.cap1", label: "Caption — main photo" },
          { key: "intro.portraits.cap2", label: "Caption — second photo" },
          { key: "intro.portraits.cap3", label: "Caption — sunlit photo" },
          { key: "intro.portraits.cap4", label: "Caption — studio photo" },
        ],
      },
      {
        label: "Manifesto",
        fields: [
          { key: "intro.manifesto.quote", label: "Signature quote", multiline: true },
          { key: "intro.manifesto.known_as", label: "Known-as line" },
          { key: "intro.manifesto.body1", label: "Paragraph 1", multiline: true },
          { key: "intro.manifesto.body2", label: "Paragraph 2", multiline: true },
          { key: "intro.manifesto.closing", label: "Closing line", multiline: true },
        ],
      },
      {
        label: "AAA Credits teaser",
        fields: [{ key: "intro.credits.line", label: "Credit line" }],
      },
    ],
  },
  {
    page: "Portfolio",
    sections: [
      {
        label: "Gallery header",
        fields: [
          { key: "portfolio.header.heading", label: "Heading" },
          { key: "portfolio.header.body", label: "Body", multiline: true },
        ],
      },
      {
        label: "Indie section",
        fields: [
          { key: "portfolio.indie.heading", label: "Heading" },
          { key: "portfolio.indie.body", label: "Body", multiline: true },
        ],
      },
      {
        label: "Screening Room",
        fields: [
          { key: "portfolio.screening.heading", label: "Heading" },
          { key: "portfolio.screening.body", label: "Body", multiline: true },
        ],
      },
      {
        label: "Closing line",
        fields: [{ key: "portfolio.cta.line", label: "Line under the AAA wall" }],
      },
    ],
  },
  {
    page: "Services",
    sections: [
      {
        label: "Header",
        fields: [
          { key: "services.header.heading", label: "Heading" },
          { key: "services.header.body", label: "Body", multiline: true },
        ],
      },
      {
        label: "Quote calculator",
        fields: [
          { key: "services.calc.heading", label: "Heading" },
          { key: "services.calc.body", label: "Body", multiline: true },
        ],
      },
    ],
  },
  {
    page: "Commission",
    sections: [
      {
        label: "Hero",
        fields: [
          { key: "commission.hero.heading", label: "Heading" },
          { key: "commission.hero.body", label: "Body", multiline: true },
        ],
      },
      {
        label: "What / Who / Why",
        fields: [
          { key: "commission.why.card1_title", label: "Card 1 title" },
          { key: "commission.why.card1_body", label: "Card 1 body", multiline: true },
          { key: "commission.why.card2_title", label: "Card 2 title" },
          { key: "commission.why.card2_body", label: "Card 2 body", multiline: true },
          { key: "commission.why.card3_title", label: "Card 3 title" },
          { key: "commission.why.card3_body", label: "Card 3 body", multiline: true },
        ],
      },
      {
        label: "Curse of Strahd",
        fields: [
          { key: "commission.strahd.heading", label: "Heading" },
          { key: "commission.strahd.body", label: "Body", multiline: true },
          { key: "commission.strahd.bullets", label: "Highlights (one per line)", multiline: true },
        ],
      },
      {
        label: "Pricing & timelines",
        fields: [
          { key: "commission.pricing.heading", label: "Heading" },
          { key: "commission.pricing.body", label: "Body", multiline: true },
        ],
      },
      {
        label: "Past work",
        fields: [
          { key: "commission.pastwork.heading", label: "Heading" },
          { key: "commission.pastwork.body", label: "Body", multiline: true },
        ],
      },
      {
        label: "Testimonials",
        fields: [{ key: "commission.testimonials.heading", label: "Heading" }],
      },
      {
        label: "Support strip",
        fields: [
          { key: "commission.support.heading", label: "Heading" },
          { key: "commission.support.body", label: "Body", multiline: true },
        ],
      },
      {
        label: "Inquiry form",
        fields: [
          { key: "commission.inquiry.heading", label: "Heading" },
          { key: "commission.inquiry.body", label: "Body", multiline: true },
        ],
      },
      {
        label: "Success message",
        fields: [
          { key: "commission.success.heading", label: "Heading" },
          { key: "commission.success.body", label: "Body", multiline: true },
        ],
      },
    ],
  },
  {
    page: "Donate modal",
    sections: [
      {
        label: "Support the Music",
        fields: [
          { key: "modal.support.heading", label: "Heading" },
          { key: "modal.support.body", label: "Body", multiline: true },
        ],
      },
    ],
  },
];
